import { apiDelete, apiGet, apiPost } from './http-client';

export type CreateBookingBody = {
  slot_id: number;
  comment?: string | null;
  payment_method?: 'online' | 'subscription' | 'cash';
  user_subscription_id?: number | null;
  return_url?: string | null;
  represented_minor_id?: number | null;
  addons?: number[];
  /** Ф8: подтверждение передачи данных (А.3) для онлайн-оплаты. */
  pd_transfer_confirmed?: boolean;
  pd_transfer_processors_hash?: string;
};

export type BookingAddonItem = {
  id: number;
  booking_id: number;
  addon_service_id: number;
  addon_name: string;
  price_cents: number;
  base_price_cents: number;
  price_overridden: boolean;
  status: string;
  added_by: number | null;
  covered_sub_id: number | null;
  created_at: string;
};

export type AllowedAddonItem = {
  id: number;
  name: string;
  price: number;
  duration_minutes: number;
  description: string | null;
  branch_id: number | null;
  covered_by_plan?: boolean;
  /** Обязательный доп: добавляется автоматически, убрать нельзя. */
  is_required?: boolean;
};

export type BookingResponse = {
  id: number;
  slot_id: number;
  service_id: number;
  status: string;
  comment: string | null;
  created_at: string;
  payment_method: string | null;
  user_subscription_id: number | null;
  payment_url: string | null;
  represented_minor_id: number | null;
  addons?: BookingAddonItem[];
  addons_total_cents?: number;
  total_cents?: number;
};

export type BookingWithSlotItem = {
  id: number;
  slot_id: number;
  service_id: number;
  status: string;
  comment: string | null;
  created_at: string;
  slot_date: string;
  start_time: string;
  end_time: string;
  service_name: string;
  cancellation_deadline_minutes: number | null;
  can_cancel: boolean;
  payment_method: string | null;
  service_payment_required: boolean;
  payment_received: boolean;
  represented_minor_id: number | null;
  represented_minor_name: string | null;
  user_subscription_id?: number | null;
  addons?: BookingAddonItem[];
  addons_total_cents?: number;
  /** Филиал слота — для уведомления А.3 на шаге оплаты. */
  slot_branch_id?: number | null;
};

export type CreatePersonalSlotBody = {
  trainer_user_id: number;
  service_id: number;
  slot_date: string;
  start_time: string;
  end_time: string;
};

export type CreatePersonalSlotResponse = {
  id: number;
};

/** POST /api/v1/bookings/personal-slot */
export function createPersonalSlot(body: CreatePersonalSlotBody, accessToken: string): Promise<CreatePersonalSlotResponse> {
  return apiPost<CreatePersonalSlotResponse>('/api/v1/bookings/personal-slot', body, { accessToken });
}

/** POST /api/v1/bookings */
export function createBooking(body: CreateBookingBody, accessToken: string): Promise<BookingResponse> {
  return apiPost<BookingResponse>('/api/v1/bookings', body, { accessToken });
}

function organizationQuery(organizationId?: number | null): string {
  return organizationId != null && organizationId > 0
    ? `?organization_id=${encodeURIComponent(String(organizationId))}`
    : '';
}

/** GET /api/v1/bookings — мои записи (клиент); organizationId — org приложения/виджета */
export function listMyBookings(
  accessToken: string,
  organizationId?: number | null
): Promise<BookingWithSlotItem[]> {
  const query = organizationId != null && organizationId > 0
    ? { organization_id: String(organizationId) }
    : undefined;
  return apiGet<BookingWithSlotItem[]>('/api/v1/bookings', query, { accessToken });
}

/** POST /api/v1/bookings/{id}/cancel */
export function cancelBooking(
  bookingId: number,
  accessToken: string,
  organizationId?: number | null
): Promise<BookingWithSlotItem> {
  return apiPost<BookingWithSlotItem>(
    `/api/v1/bookings/${bookingId}/cancel${organizationQuery(organizationId)}`,
    {},
    { accessToken }
  );
}

export type PayBookingBody = {
  payment_mode: 'online' | 'subscription' | 'cash';
  user_subscription_id?: number | null;
  return_url?: string | null;
  /** Ф8: подтверждение передачи данных (А.3) на шаге оплаты. */
  pd_transfer_confirmed?: boolean;
  pd_transfer_processors_hash?: string;
};

/** POST /api/v1/bookings/{id}/pay */
export function payBooking(
  bookingId: number,
  body: PayBookingBody,
  accessToken: string,
  organizationId?: number | null
): Promise<BookingResponse> {
  return apiPost<BookingResponse>(
    `/api/v1/bookings/${bookingId}/pay${organizationQuery(organizationId)}`,
    body,
    { accessToken }
  );
}

/** GET /api/v1/services/{id}/addons — допы, разрешённые для основной услуги */
export function fetchAllowedAddons(
  serviceId: number,
  organizationId: number,
  branchId?: number | null,
  userSubscriptionId?: number | null
): Promise<{ items: AllowedAddonItem[]; total: number }> {
  const query: Record<string, string> = { organization_id: String(organizationId) };
  if (branchId != null && branchId > 0) query.branch_id = String(branchId);
  if (userSubscriptionId != null && userSubscriptionId > 0) {
    query.user_subscription_id = String(userSubscriptionId);
  }
  return apiGet<{ items: AllowedAddonItem[]; total: number }>(
    `/api/v1/services/${serviceId}/addons`,
    query
  );
}

export type AddBookingAddonBody = {
  addon_service_id: number;
  payment_method?: 'online' | 'subscription' | 'cash';
  user_subscription_id?: number | null;
  return_url?: string | null;
};

/** POST /api/v1/bookings/{id}/addons — докинуть доп (до конца занятия — все, после — только staff) */
export function addBookingAddon(
  bookingId: number,
  body: AddBookingAddonBody,
  accessToken: string
): Promise<BookingAddonItem & { payment_url?: string | null }> {
  return apiPost<BookingAddonItem & { payment_url?: string | null }>(
    `/api/v1/bookings/${bookingId}/addons`,
    body,
    { accessToken }
  );
}

/** DELETE /api/v1/bookings/{id}/addons/{addonId} */
export function removeBookingAddon(
  bookingId: number,
  addonServiceId: number,
  accessToken: string
): Promise<void> {
  return apiDelete<void>(`/api/v1/bookings/${bookingId}/addons/${addonServiceId}`, { accessToken });
}
